import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import './styles/TodaysAppointments.css';
import './styles/CalendarHeader.css';
import ThemeToggle from '../styles/ThemeToggle';
import { formatDateWithWeekday, formatTime, formatPhone } from '../../utils/dateUtils';
import MiniMonthView from './views/MiniMonthView'; 
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Grid,
  List,
  CalendarDays,
  MapPin,
  Search,
  X,
  User,
  ExternalLink,
  ClipboardList,
  MessageSquare,
  Bell,
  History,
  Inbox
} from 'lucide-react';
import { OFFICES } from '../../constants/calendar';

const CalendarHeader = ({
  // Title and navigation props
  currentDate,
  view,
  getViewTitle,
  onNavigate,
  onViewChange,
  onTodayClick,

  // Office and stylist props
  selectedOffice,
  onOfficeChange,
  currentOfficeStaff,
  selectedStylist,
  onStylistChange,

  // Search props
  searchQuery,
  onSearchQueryChange,
  searchResults,
  showSearchResults,
  isSearching,
  onSearchResultSelect,
  onSearchClear,
  scrollToAppointment,

  // Auth props
  user,
  onLoginRequired,

  // Add props for mini month view
  appointments = [],
  holidays = [],
  onDayClick
}) => {
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const searchFieldRef = useRef(null);
  const titleRef = useRef(null); // Reference for the calendar title
  const [showMiniMonth, setShowMiniMonth] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [dropdownRect, setDropdownRect] = useState(null);

  useEffect(() => {
    if (showSearchResults && searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [showSearchResults]);

  // Handle clicking outside search results to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current && !searchInputRef.current.contains(event.target) &&
        searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)
      ) {
        onSearchClear();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearchClear]);

  // Press "/" anywhere on the calendar to jump to search (unless already typing somewhere)
  useEffect(() => {
    const handleSlash = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      e.preventDefault();
      searchFieldRef.current?.focus();
    };

    document.addEventListener('keydown', handleSlash);
    return () => document.removeEventListener('keydown', handleSlash);
  }, []);

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedResultIndex(-1);
  }, [searchResults]);

  // Scroll selected result into view when navigating with keyboard
  useEffect(() => {
    if (selectedResultIndex >= 0) {
      const selectedElement = document.querySelector('.search-result-item.keyboard-selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [selectedResultIndex]);

  const handleSearchInputChange = (e) => {
    onSearchQueryChange(e.target.value);
  };

  const handleSearchClear = () => {
    onSearchQueryChange('');
    onSearchClear();
    setSelectedResultIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSearchResults || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedResultIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedResultIndex >= 0 && selectedResultIndex < searchResults.length) {
          onSearchResultSelect(searchResults[selectedResultIndex]);
          setSelectedResultIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleSearchClear();
        break;
      default:
        break;
    }
  };

 
  // Handle title hover/click for mini month view
  const handleTitleClick = () => {
    setShowMiniMonth(!showMiniMonth);
  };

  const handleTitleMouseEnter = () => {
    setShowMiniMonth(true);
  };

  const handleTitleMouseLeave = () => {
    // Optional: auto-hide on mouse leave (you can remove this if you prefer click-only)
    // setShowMiniMonth(false);
  };

  const handleMiniMonthClose = () => {
    setShowMiniMonth(false);
  };

  const handleMiniMonthDayClick = (day) => {
    // Switch to day view and navigate to selected day
    onViewChange('day');
    if (onDayClick) {
      onDayClick(day);
    }
  };

  const pageLinks = [
    { to: '/today-appointments', label: 'Daily Schedules', Icon: ClipboardList },
    { to: '/send-text', label: 'Send Texts', Icon: MessageSquare },
    { to: '/reminders', label: 'Reminders', Icon: Bell },
    { to: '/rescheduled-appointments', label: 'Rescheduled', Icon: History },
    { to: '/leads', label: 'Website Leads', Icon: Inbox }
  ];

  const views = [
    { key: 'day', label: 'Day', Icon: List },
    { key: 'week', label: 'Week', Icon: Grid },
    { key: 'month', label: 'Month', Icon: CalendarDays }
  ];

  const userName = user?.UserName || user?.username;

  const today = new Date();
  const todayLabel = `Go to today, ${today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`;
  const hour = today.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <header className="calhdr">
      {/* Logo runs the full height of the header, beside both rows */}
      <Link to="/orderdb" className="calhdr-logo" title="Back to orders">
        <img src="/images/dini_title.png" alt="DiNi Wigs, back to orders" />
      </Link>

      <div className="calhdr-rows">
      {/* Row 1: logo, date navigation, search, account */}
      <div className="calhdr-bar">
        <div className="calhdr-lead">
        <div className="calhdr-date">
          <div className="calhdr-stepper">
            <button
              type="button"
              onClick={() => onNavigate(-1)}
              className="calhdr-nav-btn calhdr-nav-btn--prev"
              aria-label="Previous"
            >
              <ChevronLeft />
            </button>
            <button type="button" onClick={onTodayClick} className="calhdr-today" title={todayLabel}>
              <span className="calhdr-today-date" aria-hidden="true">{today.getDate()}</span>
              Today
            </button>
            <button
              type="button"
              onClick={() => onNavigate(1)}
              className="calhdr-nav-btn calhdr-nav-btn--next"
              aria-label="Next"
            >
              <ChevronRight />
            </button>
          </div>
          <h1 className="calhdr-h1">
            <button
              type="button"
              ref={titleRef}
              className="calhdr-title"
              onClick={handleTitleClick}
              onMouseEnter={handleTitleMouseEnter}
              onMouseLeave={handleTitleMouseLeave}
              aria-expanded={showMiniMonth}
              title="Pick a date"
            >
              <span className="calhdr-title-text">{getViewTitle()}</span>
              <ChevronDown aria-hidden="true" />
            </button>
          </h1>
        </div>
        </div>

        <div className="calhdr-search" ref={searchInputRef}>
          <Search className="calhdr-search-icon" aria-hidden="true" />
          <input
            ref={searchFieldRef}
            type="text"
            placeholder="Search by client name or phone"
            aria-label="Search appointments"
            aria-keyshortcuts="/"
            value={searchQuery}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyDown}
            className="calhdr-search-input"
            autoComplete="off"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={handleSearchClear}
              className="calhdr-search-clear"
              aria-label="Clear search"
            >
              <X />
            </button>
          ) : (
            <kbd className="calhdr-search-key" title="Press / to search">/</kbd>
          )}

    {/* Search Results Dropdown */}
    {showSearchResults && dropdownRect && createPortal(
      <div ref={searchDropdownRef} className="search-results-dropdown" style={{ position: 'fixed', top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width, zIndex: 10000 }}>
        {isSearching ? (
          <div className="search-loading">Searching...</div>
        ) : searchResults.length > 0 ? (
          <>
            <div className="search-results-header">
              Found {searchResults.length} appointment{searchResults.length !== 1 ? 's' : ''}
              <span className="keyboard-hint">Use ↑↓ to navigate, Enter to select</span>
            </div>
            <div className="search-results-list">
              {searchResults.map((appointment, index) => (
                <div key={appointment.ID} className="search-result-item-wrapper">
                  <div
                    className={`search-result-item ${selectedResultIndex === index ? 'keyboard-selected' : ''}`}
                    onClick={() => onSearchResultSelect(appointment)}
                  >
                    <div className="search-result-main">
                      <div className="search-result-client">{appointment.ClientName}</div>
                      <div className="search-result-date">
                        {formatDateWithWeekday(appointment.EventStart)} at {formatTime(appointment.EventStart)} {/*Wed, Jan 29, 2026 at 2:30 PM */}
                        {appointment.AppointmentStatus && (
                          <span className={`status-badge ${appointment.AppointmentStatus.toLowerCase()}`}>{appointment.AppointmentStatus}</span>
                        )}
                      </div>
                      <div>
                      </div>
                    </div>
                    <div className="search-result-details">
                      <div className="search-result-service">{appointment.Title}</div>
                      <div className="search-result-stylist">with {appointment.MeetingWith}</div>
                      {appointment.PhoneNum && (
                        <div className="search-result-phone">{formatPhone(appointment.PhoneNum)}</div>
                      )}
                    </div>
                  </div>
                  {appointment.ClientID && (
                    <button
                      className="search-result-profile-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/customers/${appointment.ClientID}`, '_blank');
                      }}
                      title="View customer profile"
                    >
                      <ExternalLink size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="search-no-results">No appointments found</div>
        )}
      </div>,
      document.body
    )}
        </div>

        <div className="calhdr-account">
          <ThemeToggle />
          {user ? (
            <div className="calhdr-user" title={`${greeting}, ${userName}`}>
              <span className="calhdr-user-dot" aria-hidden="true" />
              <span className="calhdr-sr-only">Signed in as </span>
              <span>{userName}</span>
            </div>
          ) : (
            <button
              type="button"
              className="calhdr-user calhdr-user--out"
              onClick={() => onLoginRequired && onLoginRequired(() => {})}
              title="Sign in to make changes"
            >
              <User size={15} aria-hidden="true" />
              <span>Sign in</span>
            </button>
          )}
        </div>
      </div>

      {/* Row 2: office and view switches, then links to related pages */}
      <div className="calhdr-sub">
        <div className="calhdr-segment calhdr-segment--office" role="group" aria-label="Office">
          {Object.entries(OFFICES).map(([key, office]) => (
            <button
              type="button"
              key={key}
              className={`calhdr-segment-btn ${selectedOffice === key ? 'is-active' : ''}`}
              aria-pressed={selectedOffice === key}
              onClick={() => onOfficeChange(key)}
            >
              <MapPin aria-hidden="true" />
              {office.name}
            </button>
          ))}
        </div>

        <div className="calhdr-segment calhdr-segment--view" role="group" aria-label="Calendar view">
          {views.map(({ key, label, Icon }) => (
            <button
              type="button"
              key={key}
              className={`calhdr-segment-btn ${view === key ? 'is-active' : ''}`}
              aria-pressed={view === key}
              onClick={() => onViewChange(key)}
            >
              <Icon aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        <nav className="calhdr-links" aria-label="Related pages">
          {pageLinks.map(({ to, label, Icon }) => (
            <Link key={to} to={to} className="calhdr-link">
              <Icon aria-hidden="true" />
              {label}
            </Link>
          ))}
        </nav>
      </div>

      </div>

      {/* Mini Month View */}
      <MiniMonthView
        currentDate={currentDate}
        appointments={appointments}
        holidays={holidays}
        onDayClick={handleMiniMonthDayClick}
        isVisible={showMiniMonth}
        onClose={handleMiniMonthClose}
        triggerRef={titleRef}
      />
    </header>
  );
};

export default CalendarHeader;